import type { FilteredValue } from '@fitogest/reactive-form-builder/interfaces';
import type { TratamientosControlModel } from 'src/app/models/tratamientos/tratamientos-control.model';
import { TratamientosControlValueResolver } from './tratamientos-control-value-resolver';

const CONTROL_SLOTS = [1, 2, 3] as const;

type TreatmentControlSlot = typeof CONTROL_SLOTS[number];

type RepeatableRecipeRow = Record<string, unknown>;

export interface TratamientosControlRecipeBuilderContext {
    readonly model: TratamientosControlModel;
    readonly fincas?: Array<FilteredValue<unknown>>;
    readonly productos?: Array<FilteredValue<unknown>>;
    readonly readControlValue?: (fieldName: string) => unknown;
    readonly resolveFincaLabel?: (value: unknown) => string | null;
    readonly resolveProductRawValue?: (value: unknown, rowIndex: number) => unknown | null;
}

/**
 * Builds the treatment prescription preview from the canonical repeatable rows
 * while preserving legacy slot fallbacks during the migration window.
 */
export class TratamientosControlRecipeBuilder {
    public static build(context: TratamientosControlRecipeBuilderContext): TratamientosControlModel {
        const recipe = { ...context.model } as TratamientosControlModel;
        const rows = this.getCurrentRepeatableRows(context);

        recipe.finca_nombre = this.resolveFincaName(context.model.id_finca, context);
        recipe.ids_parcelas = this.normalizeParcelIds(context.model.ids_parcelas);

        for (const slot of CONTROL_SLOTS) {
            this.patchRecipeSlot(recipe, slot, rows[slot - 1] ?? {}, context);
        }

        return recipe;
    }

    public static isParcelaSelected(parcela: FilteredValue<unknown>, idsRaw: unknown): boolean {
        const parcelaId = this.readPath(parcela, ['value', 'id']);

        if (!idsRaw || !parcelaId) {
            return false;
        }

        return this.parseIds(idsRaw).includes(String(parcelaId));
    }

    private static patchRecipeSlot(
        recipe: TratamientosControlModel,
        slot: TreatmentControlSlot,
        row: RepeatableRecipeRow,
        context: TratamientosControlRecipeBuilderContext,
    ): void {
        const rowIndex = slot - 1;
        const productValue = row['producto']
            ?? this.readSerializedRepeatableCell(context, '_linea_producto', rowIndex)
            ?? this.readModelSlotValue(context.model, `id_producto${slot}`);

        this.patchRecipeProductInfo(recipe, slot, rowIndex, productValue, context);

        const plaga = row['plaga']
            ?? this.readSerializedRepeatableCell(context, '_linea_plaga', rowIndex)
            ?? this.readModelSlotValue(recipe, `plaga${slot}`)
            ?? '';
        const dosis = row['dosis']
            ?? this.readSerializedRepeatableCell(context, '_linea_dosis', rowIndex)
            ?? this.readModelSlotValue(recipe, `dosis${slot}`)
            ?? '';
        const unit = row['udReal']
            ?? this.readSerializedRepeatableCell(context, '_linea_ud_real', rowIndex)
            ?? this.readModelSlotValue(recipe, `ud_real_${slot}`)
            ?? '';
        const dosisText = String(dosis ?? '').trim();
        const unitText = String(unit ?? '').trim();

        this.writeModelSlotValue(recipe, `plaga${slot}`, String(plaga ?? ''));
        this.writeModelSlotValue(recipe, `dosis${slot}`, dosisText !== ''
            ? `${dosisText}${unitText !== '' ? ' ' + unitText : ''}`
            : '');
    }

    private static getCurrentRepeatableRows(context: TratamientosControlRecipeBuilderContext): RepeatableRecipeRow[] {
        const rowsRaw = context.readControlValue?.('lineas_materias')
            ?? (context.model as Record<string, unknown>)['lineas_materias'];

        if (Array.isArray(rowsRaw)) {
            return rowsRaw
                .filter((row): row is RepeatableRecipeRow => row !== null && typeof row === 'object')
                .map((row) => ({ ...row }));
        }

        return CONTROL_SLOTS.map((slot) => {
            const rowIndex = slot - 1;

            return {
                producto: this.readSerializedRepeatableCell(context, '_linea_producto', rowIndex)
                    ?? this.readModelSlotValue(context.model, `id_producto${slot}`)
                    ?? '',
                uso: this.readSerializedRepeatableCell(context, '_linea_uso', rowIndex)
                    ?? this.readModelSlotValue(context.model, `uso${slot}`)
                    ?? '',
                plaga: this.readSerializedRepeatableCell(context, '_linea_plaga', rowIndex)
                    ?? this.readModelSlotValue(context.model, `plaga${slot}`)
                    ?? '',
                dosisCuaderno: this.readSerializedRepeatableCell(context, '_linea_dosis_cuaderno', rowIndex)
                    ?? this.readModelSlotValue(context.model, `dosis_cuaderno${slot}`)
                    ?? '',
                udCuaderno: this.readSerializedRepeatableCell(context, '_linea_ud_cuaderno', rowIndex)
                    ?? this.readModelSlotValue(context.model, `ud_cuaderno_${slot}`)
                    ?? '',
                dosis: this.readSerializedRepeatableCell(context, '_linea_dosis', rowIndex)
                    ?? this.readModelSlotValue(context.model, `dosis${slot}`)
                    ?? '',
                udReal: this.readSerializedRepeatableCell(context, '_linea_ud_real', rowIndex)
                    ?? this.readModelSlotValue(context.model, `ud_real_${slot}`)
                    ?? '',
            };
        });
    }

    private static resolveFincaName(rawFinca: unknown, context: TratamientosControlRecipeBuilderContext): string {
        const resolvedLabel = context.resolveFincaLabel?.(rawFinca)?.trim() ?? '';

        if (resolvedLabel !== '') {
            return resolvedLabel;
        }

        const idFinca = TratamientosControlValueResolver.resolveOptionId(rawFinca);

        if (!idFinca) {
            return '';
        }

        const found = (context.fincas ?? []).find((item) => String(this.readPath(item, ['value', 'id']) ?? '') === idFinca);

        return String(this.readPath(found, ['value', 'nombre']) ?? found?.label ?? '');
    }

    private static patchRecipeProductInfo(
        recipe: TratamientosControlModel,
        slot: TreatmentControlSlot,
        rowIndex: number,
        productValue: unknown,
        context: TratamientosControlRecipeBuilderContext,
    ): void {
        const product = this.resolveProductRawValue(productValue, rowIndex, context);

        if (!product) {
            this.writeModelSlotValue(recipe, `producto${slot}`, '');
            this.writeModelSlotValue(recipe, `materia_activa${slot}`, '');
            return;
        }

        this.writeModelSlotValue(recipe, `producto${slot}`, String(
            this.readPath(product, ['producto', 'nombre'])
                ?? this.readPath(product, ['value', 'producto', 'nombre'])
                ?? this.readPath(product, ['nombre'])
                ?? this.readPath(product, ['value', 'nombre'])
                ?? '',
        ));
        this.writeModelSlotValue(recipe, `materia_activa${slot}`, String(
            this.readPath(product, ['producto', 'materia_activa'])
                ?? this.readPath(product, ['value', 'producto', 'materia_activa'])
                ?? this.readPath(product, ['materia_activa'])
                ?? this.readPath(product, ['value', 'materia_activa'])
                ?? '',
        ));
    }

    private static resolveProductRawValue(
        value: unknown,
        rowIndex: number,
        context: TratamientosControlRecipeBuilderContext,
    ): unknown | null {
        const resolved = context.resolveProductRawValue?.(value, rowIndex) ?? null;

        if (resolved !== null && resolved !== undefined) {
            return resolved;
        }

        if (value !== null && typeof value === 'object') {
            return value;
        }

        const productId = TratamientosControlValueResolver.resolveOptionId(value);

        if (!productId) {
            return null;
        }

        const found = (context.productos ?? []).find((item) =>
            TratamientosControlValueResolver.getProductIdentityCandidates(item.value).includes(productId)
                || TratamientosControlValueResolver.getProductIdentityCandidates(item.raw).includes(productId));

        return found?.raw ?? found?.value ?? null;
    }

    private static readSerializedRepeatableCell(
        context: TratamientosControlRecipeBuilderContext,
        fieldName: string,
        rowIndex: number,
    ): string | null {
        const rawValue = context.readControlValue?.(fieldName)
            ?? (context.model as Record<string, unknown>)[fieldName]
            ?? '';
        const values = String(rawValue).split(';');
        const value = values[rowIndex]?.trim() ?? '';

        return value !== '' ? value : null;
    }

    private static normalizeParcelIds(rawIds: unknown): string {
        return this.parseIds(rawIds).join(';');
    }

    private static parseIds(rawIds: unknown): string[] {
        if (Array.isArray(rawIds)) {
            return rawIds
                .map((item) => String(this.readPath(item, ['id']) ?? item ?? '').trim())
                .filter((id) => id !== '');
        }

        if (!rawIds) {
            return [];
        }

        return String(rawIds)
            .split(/[;,]/)
            .map((id) => id.trim())
            .filter((id) => id !== '');
    }

    private static readPath(source: unknown, path: string[]): unknown {
        return path.reduce<unknown>((current, key) => {
            if (current === null || typeof current !== 'object') {
                return undefined;
            }

            return (current as Record<string, unknown>)[key];
        }, source);
    }

    private static readModelSlotValue(model: TratamientosControlModel, key: string): unknown {
        return (model as Record<string, unknown>)[key];
    }

    private static writeModelSlotValue(model: TratamientosControlModel, key: string, value: string): void {
        (model as Record<string, unknown>)[key] = value;
    }
}
